import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Inbox,
  Database,
  FileText,
  DollarSign,
  TrendingUp,
  Copy,
  GitMerge,
  Globe,
  Briefcase,
} from 'lucide-react';

const EASE_REFINED = [0.22, 1, 0.36, 1];

const CARDS = [
  {
    id: 'capture',
    Icon: Inbox,
    title: 'All your people data, connected.',
    body:
      'Stavyn links to your HRIS, performance tools, and comp records, and enriches them with live market salary and hiring data. Every employee gets one profile that stays up to date without manual exports or spreadsheets.',
    highlights: [
      { label: 'HRIS & payroll sync', Icon: Database },
      { label: 'Reviews & feedback', Icon: FileText },
      { label: 'Compensation history', Icon: DollarSign },
      { label: 'Live market benchmarks', Icon: TrendingUp },
    ],
  },
  {
    id: 'market',
    Icon: Globe,
    title: 'A live read on your market.',
    body:
      'Stavyn analyzes over 1 million open positions daily and tracks 100,000 competitor job listings, so you always know what the market pays and who is hiring your talent. Every employee still gets one clean record: deduplicated, role-matched, and searchable.',
    highlights: [
      { label: '1M+ positions analyzed daily', Icon: TrendingUp },
      { label: '100K competitor listings tracked', Icon: Briefcase },
      { label: 'One clean record per person', Icon: GitMerge },
      { label: 'Automatic deduplication', Icon: Copy },
    ],
  },
];

function HighlightList({ items, active, baseDelay }) {
  return (
    <ul
      className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 pt-5 mt-1"
      style={{ borderTop: '1px solid var(--sf-card-border)' }}
    >
      {items.map(({ label, Icon }, i) => (
        <motion.li
          key={label}
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : {}}
          transition={{ duration: 0.35, ease: EASE_REFINED, delay: baseDelay + 0.15 + i * 0.05 }}
          className="flex items-center gap-2.5"
        >
          <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--sf-label)' }} strokeWidth={2} />
          <span className="text-[12.5px] leading-snug" style={{ color: 'var(--sf-body)' }}>
            {label}
          </span>
        </motion.li>
      ))}
    </ul>
  );
}

function FoundationCard({ card, active, baseDelay }) {
  const { Icon } = card;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: EASE_REFINED, delay: baseDelay }}
      className="rounded-2xl p-6 lg:p-8"
      style={{
        background: 'var(--sf-card-bg)',
        border: '1px solid var(--sf-card-border)',
      }}
    >
      <div className="app-section-lead" aria-hidden="true">
        <Icon className="app-inline-icon app-inline-icon--md" style={{ color: 'var(--sf-label)' }} strokeWidth={1.9} />
        <span className="app-section-lead__rule" style={{ background: 'var(--sf-card-border)' }} />
      </div>

      <h3
        className="text-[clamp(1.3rem,2vw,1.6rem)] font-bold leading-tight tracking-tight mb-3 pl-3"
        style={{ color: 'var(--sf-heading)', borderLeft: '2px solid var(--sf-heading)' }}
      >
        {card.title}
      </h3>

      <p className="text-[13.5px] leading-relaxed max-w-[42ch]" style={{ color: 'var(--sf-body)' }}>
        {card.body}
      </p>

      <HighlightList items={card.highlights} active={active} baseDelay={baseDelay} />
    </motion.div>
  );
}

export default function FeaturesSection({ embedded = false }) {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-12%' });

  return (
    <section
      ref={sectionRef}
      id="capabilities"
      className="signal-foundation relative"
      style={{
        padding: 'clamp(96px, 11vw, 140px) 0',
        overflowX: 'clip',
        ...(embedded
          ? { background: 'transparent', marginTop: -2 }
          : {
              background:
                'linear-gradient(180deg, var(--sf-edge) 0%, var(--sf-deep) 15%, var(--sf-deep) 85%, var(--sf-edge) 100%)',
            }),
      }}
    >
      {!embedded && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 pointer-events-none"
          style={{
            zIndex: 0,
            top: '22%',
            bottom: '18%',
            WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 32%, #000 88%, transparent 100%)',
            maskImage: 'linear-gradient(180deg, transparent 0%, #000 32%, #000 88%, transparent 100%)',
            background:
              'radial-gradient(ellipse 120% 70% at 50% 40%, var(--sf-mist-b), transparent 72%),' +
              'radial-gradient(42% 55% at 0% 55%, var(--sf-mist-a), transparent 70%),' +
              'radial-gradient(42% 55% at 100% 55%, var(--sf-mist-a), transparent 70%)',
          }}
        />
      )}

      {/* ── Content ─────────────────────────────────────────────── */}
      <div className="relative z-[1] max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: EASE_REFINED }}
          className="flex items-center gap-4 mb-7"
        >
          <span
            className="sf-section-label eyebrow-label uppercase"
            style={{
              fontSize: '0.625rem',
              lineHeight: 1,
              letterSpacing: '0.14em',
              fontWeight: 500,
              color: 'var(--sf-label)',
              background: 'var(--sf-label-bg)',
              border: '1px solid var(--sf-heading)',
              borderRadius: 9999,
              padding: '0.3125rem 0.875rem',
              whiteSpace: 'nowrap',
            }}
          >
            The Foundation
          </span>
          <div
            aria-hidden="true"
            className="flex-1 h-px"
            style={{
              background:
                'linear-gradient(90deg, var(--sf-heading) 0%, color-mix(in srgb, var(--sf-heading) 35%, transparent) 42%, transparent 88%)',
            }}
          />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {CARDS.map((card, i) => (
            <FoundationCard key={card.id} card={card} active={inView} baseDelay={0.12 + i * 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}
